import { useEffect, useContext, useState, useMemo } from 'react'
import { Link, withRouter } from 'react-router-dom'
import { ContentBody, ContentHeader } from 'components/shared/content'
import * as H from 'history'
import {
    ActionTableCell,
    CheckboxTableRow,
    ExTable,
    SelectionFixedFooter,
    TableBody,
    TableHead,
    TableWrapper,
    useMultipleCheckForm,
} from 'components/shared/widget'
import { Dna, DnaSearchForm } from 'entities/dna'
import { Pagination } from 'components/shared/pagination'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import {
    useDownloadCsvDnaApi,
    useFetchDnaListApi,
    usePostCreateAutoDispenseSheetApi,
    usePostCreateExperimentSheetApi,
    usePostDnaToBioanalyzerApi,
    usePostDnaToDnaApi,
    usePostDnaToPcrApi,
} from 'api/dna'
import { SearchConditionView } from 'components/shared/search_drawer'
import { sampleColumnInfoList, SampleTableData } from 'components/sample/sample_table'
import { extractionColumnInfoList, ExtractionTableData } from 'components/extraction/extraction_table'
import { dnaColumnInfoList, DnaTableData } from './dna_table'
import { DisplaySetting, DisplaySettingModal, TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import { Alert, Button, Dropdown, Form, Modal, ProgressBar, Table } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { usePostToNgsApi } from 'api/dna'
import qs from 'qs'
import { CheckboxField, ChoiceField, InputField, MultipleSelectField } from 'components/shared/input'
import { MasterDataContext } from 'contexts/master_data_context'
import { TranscriptionForm } from 'components/shared/type'
import { TranscriptionModal } from 'components/shared/transcription_modal'
import { BulkEditDnaForm, EditDnaForm } from './dna_form'
import { Flex } from 'components/shared/flex'
import { downloadFile } from 'utils/util'
import { useFetchProductItemsApi } from 'api/product_item'
import { ProductItem, ProductItemSearchForm } from 'entities/product_item'
import lodash from 'lodash'
import { Probe } from 'entities/probe'
import { SampleViewModel } from 'view_models/sample_view_model'
import { usePostPcrRunApi } from 'api/pcr_run'
import { PcrRunForm } from 'entities/pcr_run'
import { ThemeContext } from 'contexts/theme_context'

type Props = {
    history: H.History
}

const DnaListPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const params = useQuery<DnaSearchForm>()
    const preSearchForm = useForm<DnaSearchForm>(params)
    const searchForm = useForm<DnaSearchForm>({
        perPage: 50,
        page: 1,
        sampleStatus: 'DNA_SYNTHESIZED',
        isUsed: false,
        ...params,
        ordering: '-sample_priority,sample_deadline_date',
    })
    const api = useFetchDnaListApi(searchForm)
    const downloadCsvApi = useDownloadCsvDnaApi(searchForm)
    const sampleDisplaySetting = useDisplaySetting(sampleColumnInfoList(searchForm), 'Dna', 'default_hidden')
    const extractionDisplaySetting = useDisplaySetting(extractionColumnInfoList(searchForm), 'Dna', 'default_hidden')
    const dnaDisplaySetting = useDisplaySetting(dnaColumnInfoList(searchForm), 'Dna')
    const displaySettings: DisplaySetting[] = [sampleDisplaySetting, extractionDisplaySetting, dnaDisplaySetting]
    const multipleCheckForm = useMultipleCheckForm<Dna>()
    const toDnaApi = usePostDnaToDnaApi()
    const toNgsApi = usePostToNgsApi()
    const toBioanalyzer = usePostDnaToBioanalyzerApi()
    const toPcr = usePostDnaToPcrApi()
    const masterData = useContext(MasterDataContext)
    const transcriptionForm = useForm<TranscriptionForm>({ items: [] })
    const [showToDnaModal, setShowToDnaModal] = useState<boolean>(false)
    const [showToNgsModal, setShowToNgsModal] = useState<boolean>(false)
    const [showToBioanalyzerModal, setShowToBioanalyzerModal] = useState<boolean>(false)
    const [showToPcrModal, setShowToPcrModal] = useState<boolean>(false)
    const [showNewPcrRun, setShowNewPcrRun] = useState<boolean>(false)
    const [editDna, setEditDna] = useState<Dna>({})
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showBulkEdit, setShowBulkEdit] = useState<boolean>(false)
    const createExperimentSheetApi = usePostCreateExperimentSheetApi()
    const createExperimentSheetForm = useForm<{ ids: string[] }>({ ids: [] })
    const [selectedProductItems, setSelectedProductItems] = useState<ProductItem[]>([])
    const productSearchForm = useForm<ProductItemSearchForm>({ scope: 'all' })
    const productItemsApi = useFetchProductItemsApi(productSearchForm)
    const postPcrRunForm = useForm<PcrRunForm>({})
    const postPcrRunApi = usePostPcrRunApi()
    const [search, setSearch] = useState('')
    const [isUnique, setIsUnique] = useState<boolean>(true)
    const createAutoDispenseSheetApi = usePostCreateAutoDispenseSheetApi()
    const createAutoDispenseSheetForm = useForm<{ ids: string[] }>({ ids: [] })
    const errorForm = useForm<{ errors?: string[]; optionalError?: string; isSuccess?: boolean }>({ errors: [] })
    const theme = useContext(ThemeContext)

    useEffectSkipFirst(() => {
        postPcrRunForm.update((f) => {
            f.errorNote = [...(errorForm.object.errors ?? []), errorForm.object.optionalError].filter((s) => !!s && s !== 'その他').join('、')
        })
    }, [errorForm.object])

    useEffect(() => {
        api.execute()
        productItemsApi.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof DnaSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(toPcr.loading)
        if (toPcr.isSuccess()) {
            api.execute()
        }
    }, [toPcr.loading])

    useEffect(() => {
        const queryParams = qs.stringify(searchForm.object)
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`dna?${queryParams}`)
        }
    }, [searchForm.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(downloadCsvApi.loading)
    }, [downloadCsvApi.loading])

    useEffect(() => {
        transcriptionForm.updateObject(
            'items',
            multipleCheckForm.form.object.items.map((item) => {
                return { id: item.code, count: 1 }
            })
        )
    }, [multipleCheckForm.form.object])

    useEffectSkipFirst(() => {
        createExperimentSheetApi.execute(createExperimentSheetForm)
    }, [createExperimentSheetForm.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(createExperimentSheetApi.loading)
        if (createExperimentSheetApi.isSuccess()) {
            const link = `https://docs.google.com/spreadsheets/${createExperimentSheetApi.response.result?.key}`
            window.open(link, '_blank')
            globalState.showConfirm(<div>OKをクリックでスプレッドシートを開きます</div>, () => {
                window.open(link, '_blank')
            })
        }
    }, [createExperimentSheetApi.loading])

    useEffectSkipFirst(() => {
        if (multipleCheckForm.form.object.items.length > 0) {
            const dna = multipleCheckForm.form.object.items[0]
            const productItemIds = dna.extraction?.sample?.productItems
                ?.filter((pi) => {
                    const completedProductItemIds = dna.extraction?.sample?.results?.map((r) => r?.productItem?.id) ?? []
                    if (completedProductItemIds.includes(pi.id)) {
                        return false
                    } else {
                        return true
                    }
                })
                .map((pi) => pi.id)
            const productItems = productItemsApi.response.results.filter((pi) => productItemIds?.includes(pi.id))
            setSelectedProductItems(productItems || [])
        } else if (multipleCheckForm.form.object.items.length === 0) {
            setSelectedProductItems([])
        }

        // productItemsのidを文字列として表現して、ユニークだったら同じプロダクトセットしか存在しない(例："1,2,3"のみだったらproduct_itemのidが1,2,3の購入者に限定されている状態)
        const idStrings: string[] = multipleCheckForm.form.object.items
            .map((dna) => {
                return dna.extraction!.sample!.productItems!.map((pi) => pi.id!).sort((a, b) => a - b)
            })
            .map((ids) => ids?.join(','))
        const isUniq = lodash.uniq(idStrings).length === 1
        setIsUnique(isUniq)
    }, [multipleCheckForm.form.object.items])

    const uniqueProductProbes = useMemo(() => {
        const probes: Probe[] = selectedProductItems
            .map((product) => {
                const currentProtocolVersionProbes =
                    (product.currentProtocolVersion?.protocolVersionProbes
                        ?.map((protocolVersionProbes) => protocolVersionProbes.probe)
                        ?.flatMap((probe) => probe) as Probe[]) || []
                const qcVersionProbes =
                    (product.qcVersion?.protocolVersionProbes
                        ?.map((protocolVersionProbes) => protocolVersionProbes.probe)
                        ?.flatMap((probe) => probe) as Probe[]) || []
                const standardUrineProbes =
                    (productItemsApi.response.results
                        .find((pi) => pi.isStandardQc)
                        ?.currentProtocolVersion?.protocolVersionProbes?.map((pvp) => pvp.probe)
                        ?.flatMap((probe) => probe) as Probe[]) || []
                return [...currentProtocolVersionProbes, ...qcVersionProbes, ...standardUrineProbes]
            })
            .flatMap((p) => p) as Probe[]
        return lodash.uniqBy(probes, (i) => i.id)
    }, [selectedProductItems])

    useEffectSkipFirst(() => {
        postPcrRunForm.update((f) => {
            f.probeIds = uniqueProductProbes.map((p) => p.id!)
            f.dnaIds = multipleCheckForm.form.object.items.map((d) => d.id!)
        })
    }, [uniqueProductProbes, multipleCheckForm.form.object.items])

    useEffectSkipFirst(() => {
        globalState.setLoading(postPcrRunApi.loading)
        if (postPcrRunApi.isSuccess()) {
            props.history.push(`/pcr_runs`)
        }
    }, [postPcrRunApi.loading])

    const WellProgressView = (props: { hideProductView?: boolean }) => {
        let probeLength = 0
        const probeThreshold = [8, 16, 24, 32, 40, 48, 56, 64, 72]
        probeThreshold.forEach((n) => {
            if (uniqueProductProbes.length <= n && probeLength === 0) {
                probeLength = n
            }
        })
        const usedWellCount: number = multipleCheckForm.form.object.items.length * probeLength * 3 + 24
        const vm = new SampleViewModel({ productItems: selectedProductItems })
        const wellSize = 384
        const variant = usedWellCount <= wellSize ? 'primary' : 'danger'
        return (
            <div>
                {selectedProductItems.length > 0 && (
                    <>
                        {isUnique ? (
                            <div style={{ fontSize: 12 }}>{vm.productItemView()}</div>
                        ) : (
                            <div style={{ fontSize: 12 }}>選択したcDNAの組み合わせでは、不要な測定が発生します</div>
                        )}
                        <Flex alignItems="center">
                            <ProgressBar max={wellSize} now={usedWellCount} style={{ flexGrow: 1 }} variant={variant} animated={usedWellCount > wellSize} />
                            <div>
                                {`${usedWellCount} / `}
                                <span style={{ fontSize: 12 }}>{wellSize} well</span>
                            </div>
                        </Flex>
                    </>
                )}
            </div>
        )
    }

    useEffectSkipFirst(() => {
        createAutoDispenseSheetApi.execute(createAutoDispenseSheetForm)
    }, [createAutoDispenseSheetForm.object])

    useEffectSkipFirst(() => {
        globalState.setLoading(createAutoDispenseSheetApi.loading)
        if (createAutoDispenseSheetApi.isSuccess()) {
            const link = `https://docs.google.com/spreadsheets/${createAutoDispenseSheetApi.response.result?.key}`
            window.open(link, '_blank')
            globalState.showConfirm(
                <div>
                    <a href={link} target="_blank" rel="noreferrer">
                        スプレッドシートを開く
                    </a>
                </div>
            )
        }
    }, [createAutoDispenseSheetApi.loading])

    return (
        <>
            <Modal show={showNewPcrRun} onHide={() => setShowNewPcrRun(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>PCR RUNを新規作成</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ marginBottom: 20 }}>
                        <WellProgressView />
                    </div>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>{multipleCheckForm.form.object.items.length + 1} cDNA</th>
                                <th>{uniqueProductProbes.length} Probe</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    {multipleCheckForm.form.object.items.map((dna) => {
                                        return <div key={'probe-' + dna.id}>{dna.code}</div>
                                    })}
                                    <div>qPCR standard</div>
                                </td>
                                <td>
                                    {uniqueProductProbes.map((probe) => {
                                        return <div key={'probe-' + probe.id}>{probe.name}</div>
                                    })}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <ChoiceField form={postPcrRunForm} attr="equipmentType" label="qPCR機器" tableName="pcr_run" />
                    <Flex>
                        <ChoiceField form={postPcrRunForm} attr="dispenser" label="自動分注機" tableName="pcr_run" />
                        <ChoiceField form={postPcrRunForm} attr="dispenserChip" label="自動分注機チップ" tableName="pcr_run" />
                    </Flex>
                    <div>
                        <MultipleSelectField
                            id="error-select"
                            form={errorForm}
                            attr="errors"
                            hideSearch
                            hideSelectAll
                            horizontal
                            label="作業時のエラー、不具合等"
                            selectItems={[
                                '試薬の入れ間違い/入れ忘れ',
                                '混和不足',
                                '蓋に触れてしまった',
                                '（増減含む）希釈ミス',
                                '試薬の融解不足',
                                '試薬はき出しきれなかった',
                                '久しぶりの作業で手が震えた',
                                'その他',
                            ].map((s) => {
                                return { label: s, value: s }
                            })}
                        />
                        {errorForm.object.errors?.includes('その他') && (
                            <InputField form={errorForm} attr="optionalError" placeholder="その他の理由を記載してください" />
                        )}
                        {(!errorForm.object.errors || errorForm.object.errors.length === 0) && !errorForm.object.optionalError && (
                            <Alert variant="success" style={{ marginTop: 10 }}>
                                <CheckboxField form={errorForm} attr="isSuccess" checkboxLabel="エラー、不具合はなかった" />
                            </Alert>
                        )}
                    </div>
                    <div>
                        <InputField form={postPcrRunForm} attr="note" as="textarea" label="備考" />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNewPcrRun(false)}>
                        閉じる
                    </Button>
                    <Button
                        onClick={() => {
                            setShowNewPcrRun(false)
                            globalState.showConfirm('qPCR RUNを開始します', () => {
                                postPcrRunApi.execute(postPcrRunForm)
                            })
                        }}
                        variant="success"
                        disabled={
                            multipleCheckForm.form.object.items.length * uniqueProductProbes.length * 3 + 24 > 384 ||
                            ((!errorForm.object.errors || errorForm.object.errors.length === 0) &&
                                !errorForm.object.optionalError &&
                                !errorForm.object.isSuccess)
                        }
                    >
                        RUN作成
                    </Button>
                </Modal.Footer>
            </Modal>
            <EditDnaForm
                dna={editDna}
                show={showEdit}
                onClose={() => setShowEdit(false)}
                onComplete={() => {
                    setShowEdit(false)
                    api.execute()
                }}
            ></EditDnaForm>

            <BulkEditDnaForm
                dnaList={multipleCheckForm.form.object.items}
                show={showBulkEdit}
                onClose={() => setShowBulkEdit(false)}
                onComplete={() => {
                    setShowBulkEdit(false)
                    api.execute()
                }}
            />
            <TranscriptionModal
                model="cDNA"
                toModel="cDNA"
                show={showToDnaModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToDnaModal(false)
                    multipleCheckForm.form.updateObject('items', [])
                    toDnaApi.execute(transcriptionForm)
                }}
                onHide={() => setShowToDnaModal(false)}
            />
            <TranscriptionModal
                model="cDNA"
                toModel="NGS"
                show={showToNgsModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToNgsModal(false)
                    multipleCheckForm.form.updateObject('items', [])
                    toNgsApi.execute(transcriptionForm)
                }}
                onHide={() => setShowToNgsModal(false)}
            />
            <TranscriptionModal
                model="cDNA"
                toModel="Bioanalyzer"
                show={showToBioanalyzerModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToBioanalyzerModal(false)
                    multipleCheckForm.form.updateObject('items', [])
                    toBioanalyzer.execute(transcriptionForm)
                }}
                onHide={() => setShowToBioanalyzerModal(false)}
                hideNote
            />
            <TranscriptionModal
                model="cDNA"
                toModel="qPCR"
                show={showToPcrModal}
                transcriptionForm={transcriptionForm}
                onClickOk={() => {
                    setShowToPcrModal(false)
                    multipleCheckForm.form.updateObject('items', [])
                    toPcr.execute(transcriptionForm)
                }}
                onHide={() => setShowToPcrModal(false)}
                hideDate
                hideNote
            />
            <ContentHeader
                headerTitle="cDNA"
                left={
                    <>
                        {/* <CopyTableButton /> */}
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} hiddenSearchParams={['sampleStatus']} />
                    </>
                }
                right={
                    <>
                        {/* <Button
                            variant="success"
                            onClick={() => {
                                globalState.showConfirm('CSV出力しますか？', () => {
                                    downloadCsvApi.execute()
                                })
                            }}
                        >
                            CSV
                        </Button> */}

                        <Form.Group controlId="show-all">
                            <Form.Check
                                type="checkbox"
                                style={{ userSelect: 'none' }}
                                label={'すべて表示'}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        searchForm.update((f) => delete f.sampleStatus)
                                    } else {
                                        searchForm.update((f) => (f.sampleStatus = 'DNA_SYNTHESIZED'))
                                    }
                                }}
                                checked={!searchForm.object.sampleStatus}
                            />
                        </Form.Group>
                        <DisplaySettingModal displaySettings={displaySettings} />
                    </>
                }
            />

            <Flex alignItems="center" style={{ marginTop: 5, height: 45 }}>
                <Link to={'/transcription_histories?type=DNA'}>転記履歴へ</Link>
                <div style={{ width: 250 }}>
                    <Form.Control
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                        }}
                        placeholder="cDNA ID(部分一致)"
                        onKeyPress={(e) => {
                            if (e.which === 13) {
                                searchForm.updateObject('dnaIdPartial', search)
                            }
                        }}
                        style={{ marginBottom: 0, width: 250 }}
                    />
                </div>
                <div style={{ flexGrow: 1 }}>
                    <WellProgressView hideProductView />
                </div>
            </Flex>
            <Flex alignItems="center" style={{ marginTop: 10 }} justifyContent="flex-end">
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper showFooter={multipleCheckForm.form.object.items.length > 0} contentHeaderCount={3}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <CheckboxTableRow head allCheck allItems={api.response.results} multipleCheckForm={multipleCheckForm}>
                                <ActionTableCell th />
                                <TableHeads displaySetting={sampleDisplaySetting} />
                                <TableHeads displaySetting={extractionDisplaySetting} />
                                <TableHeads displaySetting={dnaDisplaySetting} />
                            </CheckboxTableRow>
                        </TableHead>
                        <TableBody
                            loading={api.loading || productItemsApi.loading}
                            columnLength={
                                2 + sampleDisplaySetting.getColumnLength() + extractionDisplaySetting.getColumnLength() + dnaDisplaySetting.getColumnLength()
                            }
                        >
                            {api.response.results.map((dna: Dna) => {
                                return (
                                    <CheckboxTableRow key={`dna-${dna.id}`} item={dna} multipleCheckForm={multipleCheckForm}>
                                        <ActionTableCell
                                            action="edit"
                                            onClick={() => {
                                                setShowEdit(true)
                                                setEditDna(dna)
                                            }}
                                        />
                                        <SampleTableData sample={dna.extraction?.sample} displaySetting={sampleDisplaySetting} />
                                        <ExtractionTableData extraction={dna.extraction} displaySetting={extractionDisplaySetting} />
                                        <DnaTableData
                                            dna={dna}
                                            displaySetting={dnaDisplaySetting}
                                            isEmphasis={warningSampleList.includes(dna.extraction?.sample?.code ?? '')}
                                        />
                                    </CheckboxTableRow>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>

            <SelectionFixedFooter
                ids={multipleCheckForm.ids()}
                onClickReset={() => multipleCheckForm.form.resetForm()}
                searchForm={searchForm}
                searchAttr="dnaIdIn"
                tableName="cDNA"
            >
                <Button variant="primary" onClick={() => setShowBulkEdit(true)}>
                    一括編集
                </Button>
                <Button
                    disabled={
                        !isUnique ||
                        !(
                            multipleCheckForm.form.object.items.every((d) => warningSampleList.includes(d.extraction?.sample?.code ?? '')) ||
                            multipleCheckForm.form.object.items.every((d) => !warningSampleList.includes(d.extraction?.sample?.code ?? ''))
                        )
                    }
                    variant="success"
                    onClick={() => {
                        setShowNewPcrRun(true)
                    }}
                >
                    qPCR RUN
                </Button>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-success" id="dropdown-basic">
                        ツール
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <Dropdown.Item
                            onClick={() => {
                                globalState.showConfirm('手動cDNA作業シートを作成しますか？', () => {
                                    createExperimentSheetForm.set({ ids: multipleCheckForm.form.object.items.map((dna) => dna.code!) })
                                })
                            }}
                        >
                            手動cDNA作業シート作成
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() => {
                                globalState.showConfirm('分注機cDNA作業シートを作成しますか？', () => {
                                    createAutoDispenseSheetForm.set({ ids: multipleCheckForm.form.object.items.map((dna) => dna.code!) })
                                })
                            }}
                        >
                            分注機cDNA作業シート作成
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                let csv = ''
                                new Array(2).fill(0).map((_, index) => {
                                    csv =
                                        csv +
                                        multipleCheckForm.form.object.items
                                            .map((dna) => {
                                                return `${index === 0 ? 'Step3' : 'Step4'},${dna.code?.split('_').join(',')}`
                                            })
                                            .join('\n')
                                    if (index === 0) {
                                        csv = csv + '\n'
                                    }
                                })
                                downloadFile(csv, 'dna_id_label_print.csv', 'text/csv')
                            }}
                        >
                            ラベル印刷用CSV出力
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
                {/* <Button variant="success" onClick={() => setShowToPcrModal(true)}>
                    qPCRに転記
                </Button> */}
            </SelectionFixedFooter>
        </>
    )
}

const warningSampleList = [
    'RS_yaVGIhQ',
    'RS_mNJlVDG',
    'RS_8LmX9Lw',
    'RS_idA7e9y',
    'RS_OpHkgzj',
    'RS_cMJMO2Q',
    'RS_Tm3nOvH',
    'RS_s97ZCPN',
    'RS_e1Bd96C',
    'RS_VV8ilvr',
    'RS_3pUtDEW',
    'RS_fGHZSPo',
    'RS_4Noq9FP',
    'RS_h61tVnV',
    'RS_KqLVOGO',
    'RS_3I5qFPE',
    'RS_i8azvOL',
    'RS_DFTbRa4',
    'RS_AI3jEWQ',
    'RS_8V7LHMd',
    'RS_Lm3Y305',
    'RS_o3BOfyy',
    'RS_DNhMRnL',
    'RS_52dUwDv',
    'RS_RWRg6Yu',
    'RS_tfBXMsB',
    'RS_7YvPRDg',
    'RS_ttq97V8',
    'RS_o0BaTBc',
    'RS_ZXk3AkE',
    'RS_BBFyEs2',
    'RS_2NbXQQR',
    'RS_PaUV4p0',
    'RS_hgWxNdT',
    'RS_nDJmdCo',
    'RS_Jh119h4',
    'RS_bD18E5Z',
    'RS_0Gv6FYg',
    'RS_7lxlKnN',
    'RS_fZCkxuL',
    'RS_5dix87g',
    'RS_E1wh24n',
    'RS_n19KMRG',
    'RS_hApvhPQ',
    'RS_DfkqqUl',
    'RS_8N6VKRM',
    'RS_id1zXtz',
    'RS_2L0gmPC',
    'RS_Ce9I7us',
    'RS_YDunsH1',
    'RS_aRI9Ax2',
    'RS_YtJFTBe',
    'RS_5GilSRb',
    'RS_cUEfpyC',
    'RS_lcjq4Bl',
    'RS_PCSSUn8',
    'RS_ef2tqDI',
    'RS_oRI4ioB',
    'RS_sbcxOZU',
    'RS_eQQg48G',
    'RS_OZnsRsh',
    'RS_dq0F2wB',
    'RS_2rEGD0d',
    'RS_nxXWZiN',
    'RS_h3wDSHZ',
    'RS_NrhQIZz',
    'RS_MRKBnCi',
    'RS_1jvuFWF',
    'RS_A8XChjS',
    'RS_MJ9joBz',
    'RS_HYhrwtq',
    'RS_7VBeJR3',
    'RS_dzUdAbB',
    'RS_T6hKJl5',
    'RS_4JBGFWH',
    'RS_F0Zhvtg',
    'RS_wN1KE6N',
    'RS_CokjpfT',
    'RS_OilxHba',
    'RS_khO40Bp',
    'RS_Q8iZyHc',
    'RS_ygcQKMy',
    'RS_HLNCoxq',
    'RS_zd6wsiC',
    'RS_hyuhi3a',
    'RS_3qnyNY6',
    'RS_Yq7NGG6',
    'RS_eqdPMmq',
    'RS_VqadR76',
    'RS_Q6EJmeq',
    'RS_Wx5Z0b6',
    'RS_N6wwgX6',
    'RS_jqbQDE6',
    'RS_VxMMJZx',
    'RS_ak94p7q',
    'RS_D6RrXv6',
    'RS_KkOMbN6',
    'RS_PqgMDVx',
]

export default withRouter(DnaListPage)
